import { Callout, Select, Typography, YStack } from '@shinetools/lumen-react'
import { Sparkles } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchAccounts } from '@features/accounts/hooks/useFetchAccounts'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useTaxRates } from '@modules-deprecated/app/taxrates'
import { AutomationUpgradeSection } from '@views/suppliers/elements/AutomationUpgradeSection'

// The @components barrel re-exports CreateContactModal, which renders this section through the create-supplier modal.
// Moving the supplier modal out of views/ does not fix this; it can import from @components again once the barrel
// stops re-exporting CreateContactModal and ContactSelect (EXP-660).
// eslint-disable-next-line @billy/shine-accounting/consistent-alias-imports -- importing the barrel from here cycles back through CreateContactModal
import { useBookkeepingCategories } from '../../../../../../../../../../../../components/selects/BookkeepingHeroSelect/hooks/useBookkeepingCategories'
import { DesignSystem } from '../../../../../../../../../../../../enums/designSystem'
import { Scope } from '../../../../../../../../../../../../enums/scope'
import { useFormContext, useWatch } from '../../../../../../../../../../../../hooks/useForm'
import type { WithTrackingContext } from '../../../../../../../../../../../../types/withTrackingContext'
import { getCurrentLocale } from '../../../../../../../../../../../../utils'
import { isAuthorized } from '../../../../../../../../../../../../utils/isAuthorized'
import type { SupplierFormSchema } from '../../../../types/supplierForm'
import { getPurchaseCategoryOptions } from './utils/getPurchaseCategoryOptions'
import { getPurchaseDescriptionOptions } from './utils/getPurchaseDescriptionOptions'
import { getPurchaseTaxRateOptions } from './utils/getPurchaseTaxRateOptions'
import { withNoDefaultOption } from './utils/withNoDefaultOption'

export const AutomationSection = ({ trackingContext }: WithTrackingContext) => {
  const { t } = useTranslation()
  const { control, FormItem } = useFormContext<SupplierFormSchema, DesignSystem.Lumen>({
    designSystem: DesignSystem.Lumen,
  })
  const [countryId, description, expenseAccountId, taxRateId] = useWatch({
    control,
    name: ['countryId', 'defaultExpenseProductDescription', 'defaultExpenseAccountId', 'defaultTaxRateId'],
  })
  const locale = getCurrentLocale()
  const { organization } = useUserOrganization()
  const { taxRates = [], isLoading: isTaxRatesLoading } = useTaxRates()
  const organizationId = organization?.id
  const isDanishContact = countryId === 'DK'
  const isAutomationAuthorized = isAuthorized(Scope.ContactBookkeepingDefaults)
  const noDefaultLabel = t('suppliers.detail.form.default_option.none')

  const { isLoading: isExpenseCategoriesLoading, suggestedCategories } = useBookkeepingCategories({
    isEnabled: !!organizationId,
    locale,
  })
  const { accounts, isLoading: isAccountsLoading } = useFetchAccounts({
    enabled: isDanishContact && !!organizationId,
  })

  const descriptionOptions = useMemo(
    () =>
      withNoDefaultOption(getPurchaseDescriptionOptions(suggestedCategories, description), description, noDefaultLabel),
    [description, noDefaultLabel, suggestedCategories],
  )
  const categoryOptions = useMemo(
    () => withNoDefaultOption(getPurchaseCategoryOptions(accounts, expenseAccountId), expenseAccountId, noDefaultLabel),
    [accounts, expenseAccountId, noDefaultLabel],
  )
  const taxRateOptions = useMemo(
    () => withNoDefaultOption(getPurchaseTaxRateOptions(taxRates), taxRateId, noDefaultLabel),
    [noDefaultLabel, taxRateId, taxRates],
  )

  if (!isAutomationAuthorized) {
    return <AutomationUpgradeSection trackingContext={trackingContext} />
  }

  return (
    <YStack gap={24}>
      <Typography.Text size="large" weight="semibold">
        {t('suppliers.detail.automation.title')}
      </Typography.Text>
      <Callout color="informative" icon={Sparkles} message={t('suppliers.detail.automation.callout.hint')} />
      <FormItem
        name="defaultExpenseProductDescription"
        render={({ field }) => (
          <Select
            disabled={isExpenseCategoriesLoading}
            label={t('suppliers.detail.form.default_purchase_description')}
            onChange={field.onChange}
            options={descriptionOptions}
            placeholder={t('suppliers.detail.form.default_purchase_description.placeholder')}
            searchable
            size="small"
            value={field.value}
          />
        )}
      />
      {isDanishContact ? (
        <FormItem
          name="defaultExpenseAccountId"
          render={({ field }) => (
            <Select
              disabled={isAccountsLoading}
              label={t('suppliers.detail.form.default_purchase_category')}
              onChange={field.onChange}
              options={categoryOptions}
              placeholder={t('suppliers.detail.form.default_purchase_category.placeholder')}
              searchable
              size="small"
              value={field.value}
            />
          )}
        />
      ) : null}
      <FormItem
        name="defaultTaxRateId"
        render={({ field }) => (
          <Select
            disabled={isTaxRatesLoading}
            label={t('suppliers.detail.form.default_vat_rate.label')}
            onChange={field.onChange}
            options={taxRateOptions}
            placeholder={t('suppliers.detail.form.default_vat_rate.placeholder')}
            searchable
            size="small"
            value={field.value}
          />
        )}
      />
    </YStack>
  )
}
